import * as vp from 'vopay';
import { Box, Table, Button, Icon, usePagination, Pagination } from 'vopay';
import moment from 'moment';
import { FunctionComponent, JSX, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './AccountingSystemClosed.module.scss';
import { Payment } from '../AccountingSystemOpen/types';
import PartiallyPaidDialogue from '../AccountingSystemOpen/PartiallyPaidDialogue/PartiallyPaidDialogue';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

type AccountingSystemContainerProps = {
  BillsOrInvoices: vp.BillOrInvoiceRailz[];
  ServiceName: string;
  Type: 'Payable' | 'Receivable';
};
type FC = FunctionComponent<AccountingSystemContainerProps>;

const AccountingSystemClosed: FC = ({ BillsOrInvoices, Type, ServiceName }: AccountingSystemContainerProps) => {
  const [showPartiallyPaidDialogue, setShowPartiallyPaidDialogue] = useState(false);
  const [partiallyPaidPayments, setPartiallyPaidPayments] = useState<Payment[]>([]);

  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.CloseTab' });

  // pagination
  const totalDataLength = useMemo(() => {
    return BillsOrInvoices.length || 0;
  }, [BillsOrInvoices]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const showPartiallyPaid = (invoiceOrBill: vp.BillOrInvoiceRailz) => {
    setShowPartiallyPaidDialogue(true);
    setPartiallyPaidPayments(
      (invoiceOrBill.PaymentDetails || []).map((payment) => ({
        date: payment.TransactionDate
          ? moment(payment.TransactionDate).format('MMM DD, YYYY')
          : moment().format('MMM DD, YYYY'),
        amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(
          String(
            Number(vp.sanitizeToNumeric(String(payment.TransactionAmount))) +
              Number(vp.sanitizeToNumeric(String(payment.CreditAmount))),
          ),
        )}`,
        currency: invoiceOrBill.currency,
        transactionId: payment.TransactionID,
        details: [
          {
            label: payment.TransactionID ? (
              payment.PaymentMethod
            ) : (
              <Box gap="sm" base="8" ai="center">
                <Icon icon="accountingSoftware" color="none" height="20px" width="20px" />{' '}
                {t('paidViaPaymentMethods', { serviceName: ServiceName })}
              </Box>
            ),
            amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(String(payment.TransactionAmount))}`,
            visible: payment.TransactionAmount && Number(vp.sanitizeToNumeric(String(payment.TransactionAmount))) > 0,
          },
          {
            label: (
              <Box gap="sm" base="8" ai="center">
                <Icon icon="accountingCredits" color="none" height="20px" width="20px" /> {t('creditsApplied')}
              </Box>
            ),
            amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(String(payment.CreditAmount))}`,
            visible: payment.CreditAmount && Number(vp.sanitizeToNumeric(String(payment.CreditAmount))) > 0,
          },
        ].filter((detail) => detail.visible),
      })),
    );
  };
  const tableProps: vp.TableProps<vp.BillOrInvoiceRailz> = {
    id: 'closed-bills-invoices',
    version: '2',
    columns: [
      {
        id: 'duedate',
        header: Type === 'Receivable' ? t('ColumnHeader.invoiceDate') : t('ColumnHeader.billDate'),
        align: 'left',
        template: ({ data }) => format(new Date(data.postedDate.replace('Z', '')), 'yyyy-MM-dd'),
      },
      {
        id: 'name',
        header: Type === 'Receivable' ? t('ColumnHeader.customer') : t('ColumnHeader.supplier'),
        align: 'left',
        template: ({ data }) => data.Customer?.Name || data?.customerRef?.name || data?.vendorRef?.name || '',
      },
      {
        id: 'invoiceNumber',
        header: Type === 'Receivable' ? t('ColumnHeader.invoiceNumber') : t('ColumnHeader.billNumber'),
        align: 'left',
        template: ({ data }) => data.invoiceNumber || data.vendorInvoiceNumber || '',
      },
      {
        id: 'amount',
        header: t('ColumnHeader.amount'),
        align: 'left',
        template: ({ data }) => {
          const paymentDetails = data.PaymentDetails;
          const hasMoreThanOnePayment =
            paymentDetails.length > 1 ||
            paymentDetails.some((p) => Number(p.CreditAmount) > 0 && Number(p.TransactionAmount) > 0);

          return (
            <Box flx="col" gapSize="4px">
              <Box flx="row" gapSize="6px">
                <span>{`${data?.currency || ''} ${vp.formattedCurrency(data.totalAmount.toString())}`}</span>
                {hasMoreThanOnePayment && (
                  <Button
                    label=""
                    type="button"
                    width="18px"
                    height="18px"
                    background="transparent"
                    color="none"
                    icon="information"
                    iconColor="medium-grey"
                    iconLocation="center"
                    iconWidth="12px"
                    iconHeight="12px"
                    iconSize="xsm"
                    textAlign="center"
                    borderColor="#C4C4C4"
                    noPadding
                    noShadow
                    isCircle
                    onClick={() => showPartiallyPaid(data)}
                  />
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        id: 'lastPaymentDate',
        header: t('ColumnHeader.lastPaymentDate'),
        align: 'left',
        template: ({ data }) => getLastPaymentDate(data.PaymentDetails, data.postedDate),
      },
      {
        id: 'paymentMethod',
        header: t('ColumnHeader.paymentMethod'),
        align: 'left',
        template: ({ data }) => getPaymentMethodFromPayments(data.PaymentDetails),
      },
      {
        id: 'transactionId',
        header: t('ColumnHeader.vopayTransactionId'),
        align: 'left',
        template: ({ data }) => getVopayTransactionIds(data.PaymentDetails, data.currency || '', ServiceName),
      },
    ],
    rows: BillsOrInvoices.map((item, i) => ({
      id: item.id || String(i),
      data: item,
    })).slice(offset, offset + PER_PAGE),
  };

  return (
    <Box flx="col" width="100%">
      <Box>
        <Table {...tableProps} />
      </Box>
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={({ selected }) => setPageIndex(selected)}
          />
        )}
      </Box>
      <PartiallyPaidDialogue
        isVisible={showPartiallyPaidDialogue}
        payments={partiallyPaidPayments}
        close={() => setShowPartiallyPaidDialogue(false)}
      />
    </Box>
  );
};
function getPaymentMethodFromPayments(payments: vp.BillOrInvoiceRailzPaymentDetails[]): string {
  const paymentMethods = payments.map((payment) => (payment.PaymentMethod ? payment.PaymentMethod : null));
  const uniquePaymentMethods = [...new Set(paymentMethods)];
  if (uniquePaymentMethods.length === 0) {
    return '';
  }
  // Case 1: All payments have the same PaymentMethod and CreditAmount is 0 or null
  const allCreditAmountsZeroOrNull = payments.every(
    (payment) => Number(payment.CreditAmount) === 0 || payment.CreditAmount === null,
  );

  if (uniquePaymentMethods.length === 1 && allCreditAmountsZeroOrNull) {
    return uniquePaymentMethods[0] ?? '';
  }

  // Case 2: All payments have 0 or null for PaymentMethod but CreditAmount is not null
  const allCredits = payments.every(
    (payment) =>
      payment.CreditAmount !== null &&
      Number(payment.CreditAmount) > 0 &&
      (Number(payment.TransactionAmount) === 0 || !payment.TransactionAmount),
  );
  if (allCredits) {
    return 'Paid using Credits';
  }

  // Case 3: No payment methods supplied or all are blank, and CreditAmount is blank
  if (uniquePaymentMethods.length === 0 || uniquePaymentMethods.every((method) => method === null)) {
    const allCreditAmountsNull = payments.every((payment) => !payment.CreditAmount);
    if (allCreditAmountsNull) {
      return '';
    }
  }

  // Case 4: Otherwise, multiple payment methods
  return 'Multiple';
}
function getVopayTransactionIds(
  payments: vp.BillOrInvoiceRailzPaymentDetails[],
  currency: string,
  serviceName: string,
): string | JSX.Element {
  const paymentMethods = getPaymentMethodFromPayments(payments);
  if (paymentMethods !== 'Paid using Credits') {
    const transactionLinks = payments
      .filter((payment) => payment.TransactionID && payment.TransactionID !== '')
      .map((payment) => (
        <Link to={`/transactions-history/details/Transaction/${payment.TransactionID || ''}/${currency}`}>
          <div className={styles.partiallyPaidLink}>{payment.TransactionID}</div>
        </Link>
      ));
    if (transactionLinks.length > 0) {
      return (
        <Box>
          {transactionLinks.map((transaction, index) => (
            <>
              {transaction}
              {index !== transactionLinks.length - 1 && ', '}
            </>
          ))}
        </Box>
      );
    }
    return (
      <>
        Paid using <br />
        {serviceName}
      </>
    );
  }
  return '';
}

function getLastPaymentDate(payments: vp.BillOrInvoiceRailzPaymentDetails[], invoiceDate: string): string {
  const paymentDates = payments.map((payment) => (payment.TransactionDate ? payment.TransactionDate : ''));
  const lastPaymentDate = paymentDates.reduce((a, b) => (a > b ? a : b), '');
  const invoiceDateFormatted = moment(invoiceDate).format('YYYY-MM-DD');
  const finalDate = lastPaymentDate ? moment(lastPaymentDate).format('YYYY-MM-DD') : invoiceDateFormatted;
  return finalDate;
}

export default AccountingSystemClosed;
